import { Button } from "@/src/components/Button/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/shad/card";
import InputComponent from "@/src/components/Input/Input";
import { useMutation } from "@tanstack/react-query";
import api, { getFlagEmoji } from "@/src/utils";
import { z } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal } from "@/src/components/Modal/Modal";
import useUrlState from "@/src/hooks/use-url-state";
import apiRoutes from "@/src/apiRoutes";
import { toast } from "sonner";
import { useCountries } from "@/src/hooks/useCountries";
import PhoneInput from "@/src/components/PhoneInput/AppendInput";
import { textVariants } from "@/src/components/Text/Text";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@components/shad/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/shad/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

const formState = z
  .object({
    phone: z.string(),
    email_code: z
      .string()
      .length(6, { message: "Verification code should be exactly 6 digits" })
      .optional(),
    country_code: z
      .string()
      .min(2, { message: "Enter a valid length for country code" }),
  })
  .refine((data) => data.country_code, {
    path: ["phone"],
  });

const ChangePhone = () => {
  type deactivateType = z.infer<typeof formState>;
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useForm<deactivateType>({
    resolver: zodResolver(formState),
  });
  const { countries } = useCountries();
  const [modal, setModal] = useUrlState("modal");

  const changePhone = async (data: deactivateType) => {
    const res = await api.post(apiRoutes.phoneChange, {
      ...data,
      // phone: `${data?.country_code.trim()}${data?.phone.trim()}`
    });
    return res.data;
  };

  const { mutate, isLoading } = useMutation({
    mutationKey: ["change_phone"],
    mutationFn: changePhone,
    onSuccess: (data) => {
      if (data?.success) {
        setModal(undefined);
        toast.success(data?.info);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const sendPhoneOtp = async () => {
    const res = await api.get(apiRoutes.phoneChange);
    return res.data;
  };

  const { mutate: sendOtp, isLoading: otpLoad } = useMutation({
    mutationFn: sendPhoneOtp,
    mutationKey: ["send_phone_otp"],
    onSuccess: (data) => {
      if (data?.success) {
        setModal("phone");
        toast.success(data?.info);
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const onSubmit: SubmitHandler<deactivateType> = () => {
    sendOtp();
  };
  const selectedCountry = countries?.info.find(
    (item) => item?.short_code_iso3 === watch("country_code")
  );

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle>Change Phone Number</CardTitle>
          {/* <CardDescription>Disable USSD service on your phone number</CardDescription> */}
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="grid gap-2">
            <div className="flex items-center">
              <PhoneInput
                type="number"
                label="Enter  phone number"
                selectOption={
                  <Popover>
                    <div className=" grid ">
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className={cn(
                            " flex justify-between text-black font-medium w-full items-center border-gray-200 bg-input-field rounded-md px-3 py-2.5 text-sm  shadow-none dark:bg-grey-500 dark:text-white whitespace-nowrap border-0"
                          )}
                        >
                          {selectedCountry ? (
                            <>
                              {getFlagEmoji(
                                selectedCountry?.short_code as string
                              )}{" "}
                              +{selectedCountry?.short_code_iso3}
                            </>
                          ) : (
                            <span>Eg. +233</span>
                          )}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 " />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className={cn(
                          ` max-h-[--radix-popover-content-available-height] overflow-y-scroll p-0 min-w-[var(--radix-popover-trigger-width)]  z-[100] `
                        )}
                        align="start"
                      >
                        <Command>
                          <CommandInput placeholder={`Search country`} />
                          <CommandEmpty>Nothing found.</CommandEmpty>
                          <CommandGroup>
                            {countries?.info.map((item, index) => (
                              <CommandItem
                                key={index}
                                onSelect={() => {
                                  setValue(
                                    "country_code",
                                    item?.short_code_iso3
                                  );
                                }}
                                value={item?.name + item?.short_code_iso3}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    watch("country_code") ===
                                      item?.short_code_iso3
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />

                                <div className="flex items-center h-1">
                                  <span className="text-lg mr-2">
                                    {getFlagEmoji(item?.short_code)}
                                  </span>
                                  <span>
                                    {item?.name} (+{item?.short_code_iso3})
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </div>
                  </Popover>
                }
                autoComplete="off"
                errors={errors.phone?.message}
                {...register("phone", { required: true })}
              />
            </div>
          </CardContent>
          <CardHeader className=" bg-back p-2 flex flex-row justify-between items-center border-t rounded-b-lg">
            <span className={textVariants({ variant: "desc", font: "medium" })}>
              Provide details to change existing phone number
            </span>

            <Button
              onClick={handleSubmit(onSubmit)}
              loading={otpLoad}
              title="Change"
              variant={"primary"}
              type={"submit"}
            >
              Change
            </Button>
          </CardHeader>
        </form>
      </Card>
      <Modal
        open={modal === "phone"}
        setOpen={(val) => setModal(val ? undefined : "phone")}
        title="Verify code sent to email"
        description="A verification code has been sent to your email. Kindly check and verify."
        footer={
          <div>
            <Button
              loading={isLoading}
              onClick={() => mutate(getValues())}
              variant={"outline"}
            >
              Submit
            </Button>
          </div>
        }
        size={"sm"}
      >
        <div>
          <InputComponent
            onChange={(e) => setValue("email_code", e.target.value)}
            label="Enter otp sent to your email"
            type="number"
          />
        </div>
      </Modal>
    </div>
  );
};

export default ChangePhone;
